import { useEffect, useState, lazy, Suspense } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Input } from "../components/ui/input.jsx";
import { Card } from "../components/ui/card.jsx";
import { Button } from "../components/ui/button.jsx";
import { useToast } from "../hooks/use-toast";
import SeasonCalendar from "../components/SeasonCalendar.jsx";

// Lazy load the map component to avoid issues if Leaflet isn't loaded

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format dates like "Sep 15 – Jan 31"
function formatSeasonRange(start, end){
  const fmt = (s)=>{
    if(!s) return null;
    // If already looks like "Month Day" or has month abbrev, keep as-is
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    // ISO date case
    if(/^\d{4}-\d{2}-\d{2}$/.test(s)){
      const d=new Date(s);
      if(!isNaN(d)) return `${monthNames[d.getMonth()]} ${d.getDate()}`;
    }
    // Full month name like "October 7" or with comma year
    const m = s.match(/([A-Za-z]+)\.?\s+(\d{1,2})/);
    if(m){
      const mon = m[1].slice(0,3);
      return `${mon.charAt(0).toUpperCase()+mon.slice(1).toLowerCase()} ${m[2]}`;
    }
    // Month abbreviation like "Oct 7"
    const m2 = s.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\.?\s*(\d{1,2})/i);
    if(m2){
      const mon = m2[1].slice(0,3);
      return `${mon.charAt(0).toUpperCase()+mon.slice(1).toLowerCase()} ${m2[2]}`;
    }
    // Fallback to raw string trimmed
    return String(s).replace(/,?\s*\d{4}/,'').replace(/\s+/g,' ').trim();
  };
  const a = fmt(start);
  const b = fmt(end);
  if(a && b) return `${a} – ${b}`; // en dash
  if(a) return a;
  if(b) return b;
  return '—';
}

// Helper to extract dates from season titles as an array
function extractDatesFromTitle(title) {
  if (!title) return [];
  
  const monthAbbrev = {
    'january': 'Jan', 'february': 'Feb', 'march': 'Mar', 'april': 'Apr',
    'may': 'May', 'june': 'Jun', 'july': 'Jul', 'august': 'Aug',
    'september': 'Sep', 'october': 'Oct', 'november': 'Nov', 'december': 'Dec'
  };
  
  let extractedDates = [];
  
  // Find all date ranges like "October 18 through October 31"
  let matches = [...title.matchAll(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+([A-Za-z]+)\s+(\d{1,2})/gi)];
  matches.forEach(match => {
    const startMonth = monthAbbrev[match[1].toLowerCase()] || match[1].slice(0,3);
    const startDay = match[2];
    const endMonth = monthAbbrev[match[3].toLowerCase()] || match[3].slice(0,3);
    const endDay = match[4];
    extractedDates.push(`${startMonth} ${startDay} – ${endMonth} ${endDay}`);
  });
  
  // Find date pairs like "November 26 and 27"
  matches = [...title.matchAll(/([A-Za-z]+)\s+(\d{1,2})\s+and\s+(\d{1,2})(?!\s+through)/gi)];
  matches.forEach(match => {
    const month = monthAbbrev[match[1].toLowerCase()] || match[1].slice(0,3);
    const day1 = match[2];
    const day2 = match[3];
    extractedDates.push(`${month} ${day1} & ${day2}`);
  });
  
  // Find patterns like "January 10 through 24" (same month range)
  matches = [...title.matchAll(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+(\d{1,2})(?!\s*[A-Za-z])/gi)];
  matches.forEach(match => {
    const month = monthAbbrev[match[1].toLowerCase()] || match[1].slice(0,3);
    const startDay = match[2];
    const endDay = match[3];
    extractedDates.push(`${month} ${startDay} – ${endDay}`);
  });
  
  // Find standalone dates that aren't part of ranges
  const cleanTitle = title.replace(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+([A-Za-z]+)\s+(\d{1,2})/gi, '')
                         .replace(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+(\d{1,2})/gi, '')
                         .replace(/([A-Za-z]+)\s+(\d{1,2})\s+and\s+(\d{1,2})/gi, '');
  matches = [...cleanTitle.matchAll(/([A-Za-z]+)\s+(\d{1,2})(?!\s+through|\s+and)/gi)];
  matches.forEach(match => {
    const month = monthAbbrev[match[1].toLowerCase()] || match[1].slice(0,3);
    const day = match[2];
    extractedDates.push(`${month} ${day}`);
  });
  
  return extractedDates;
}

// Helper to clean season titles by removing date information
function cleanSeasonTitle(title) {
  if (!title) return title;
  
  // Remove common date patterns from titles
  return title
    // Remove patterns like ": October 18 through October 31, and November 27"
    .replace(/:\s*([A-Za-z]+\s+\d{1,2}.*)/gi, '')
    // Remove patterns that start with dates
    .replace(/([A-Za-z]+\s+\d{1,2}.*)/gi, '')
    // Clean up extra whitespace and trailing colons/dashes
    .replace(/[:\-–—]\s*$/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Helper to parse a date string into a Date object for sorting
// Uses hunting season calendar: Aug → Sep → Oct → Nov → Dec → Jan → Feb → Mar → Apr → May → Jun → Jul
function parseDateForSorting(dateStr) {
  if (!dateStr) return null;
  
  const monthMap = {
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'sept': 8, 'oct': 9, 'nov': 10, 'dec': 11
  };
  
  // Match patterns like "Oct 1", "October 15", "Jan 31"
  const match = dateStr.match(/([A-Za-z]+)\.?\s+(\d{1,2})/i);
  if (match) {
    const monthStr = match[1].toLowerCase().slice(0, 4);
    const day = parseInt(match[2], 10);
    const month = monthMap[monthStr];
    
    if (month !== undefined) {
      // Use a consistent year for all months to ensure proper sorting
      // Hunting seasons typically run Aug-Jul, so adjust months before Aug to sort later
      // Aug (7) = 0, Sep (8) = 1, Oct (9) = 2, Nov (10) = 3, Dec (11) = 4,
      // Jan (0) = 5, Feb (1) = 6, Mar (2) = 7, Apr (3) = 8, May (4) = 9, Jun (5) = 10, Jul (6) = 11
      const huntingMonthOffset = month >= 7 ? month - 7 : month + 5;
      // Use base year 2025 for all dates, but with month offset to ensure Aug < Sep < ... < Jul ordering
      return new Date(2025, huntingMonthOffset, day);
    }
  }
  
  // Try ISO format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr);
  }
  
  return null;
}

// Helper to get the earliest date from season data
function getEarliestSeasonDate(season) {
  // Try extracting dates from title first
  const extractedDates = extractDatesFromTitle(season.title);
  if (extractedDates.length > 0) {
    // Parse all extracted dates and find the earliest
    const dates = extractedDates
      .map(dateRange => {
        // Handle ranges like "Oct 1 – Oct 15" or "Oct 1 & 2"
        const parts = dateRange.split(/[–—&]/);
        return parseDateForSorting(parts[0].trim());
      })
      .filter(d => d !== null);
    
    if (dates.length > 0) {
      return new Date(Math.min(...dates.map(d => d.getTime())));
    }
  }
  
  // Fall back to start_date field
  if (season.start_date) {
    const parsed = parseDateForSorting(season.start_date);
    if (parsed) return parsed;
  }
  
  // If no valid date found, return a far future date to push to end
  return new Date(9999, 11, 31);
}

export default function StateHub() {
  const { code } = useParams();
  const { toast } = useToast();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search] = useSearchParams();
  const [tab, setTab] = useState(search.get('tab') || "regulations");
  const [error, setError] = useState("");
  const [resourceFilter, setResourceFilter] = useState("All");
function useInput(initial){ const [v,setV]=useState(initial); return { value:v, onChange:e=>setV(e.target.value), set:setV }; }
function ResourcesControls({ stateCode, initialFilter = "All" }){
  const zipCode = useInput("");
  const type = useInput(initialFilter);
  const searchName = useInput("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [facilityFilters, setFacilityFilters] = useState({
    camping: false,
    boat: false,
    range: false,
    hiking: false,
    birding: false,
    fishing: false
  });
  
  // Resource type configurations with icons and colors
  const resourceTypes = {
    'All': { icon: '🔍', color: 'bg-gray-600/30', label: 'All Resources' },
    'Wildlife Areas': { icon: '🌲', color: 'bg-green-600/30', label: 'Wildlife Areas' },
    'License Agents': { icon: '📋', color: 'bg-blue-600/30', label: 'License Agents' },
    'Shooting Ranges': { icon: '🎯', color: 'bg-red-600/30', label: 'Shooting Ranges' },
    'Hunter Ed / Classes': { icon: '🎓', color: 'bg-purple-600/30', label: 'Education' }
  };

  const fetchItems = async () => {
    try { 
      setLoading(true); 
      const params = { state_code: stateCode };
      if(zipCode.value.trim()) params.zip_code = zipCode.value.trim();
      if(type.value && type.value !== 'All') params.type = type.value;
      if(searchName.value.trim()) params.name = searchName.value.trim();
      
      const res = await axios.get(`${API}/resources`, { params });
      setItems(res.data?.items || []);
    } finally { 
      setLoading(false); 
    }
  };

  const toggleFavorite = (resourceId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(resourceId)) {
      newFavorites.delete(resourceId);
    } else {
      newFavorites.add(resourceId);
    }
    setFavorites(newFavorites);
    // Save to localStorage
    localStorage.setItem(`favorites-${stateCode}`, JSON.stringify([...newFavorites]));
  };

  const getDirectionsUrl = (address) => {
    const encoded = encodeURIComponent(address);
    return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    // Simple phone formatting
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  useEffect(() => { 
    fetchItems(); 
    // Load favorites from localStorage
    const saved = localStorage.getItem(`favorites-${stateCode}`);
    if (saved) {
      setFavorites(new Set(JSON.parse(saved)));
    }
  }, [stateCode]);
  
  const filteredItems = items.filter(item => {
    // Filter by name
    if (searchName.value.trim()) {
      if (!item.name.toLowerCase().includes(searchName.value.toLowerCase())) return false;
    }
    
    // Apply facility filters for Wildlife Areas
    if (type.value === 'Wildlife Areas') {
      const anyFacilitySelected = Object.values(facilityFilters).some(v => v);
      if (anyFacilitySelected) {
        let hasSelectedFacility = false;
        if (facilityFilters.camping && item.primitive_camping) hasSelectedFacility = true;
        if (facilityFilters.boat && item.boat_ramps) hasSelectedFacility = true;
        if (facilityFilters.range && item.shooting_ranges) hasSelectedFacility = true;
        if (facilityFilters.hiking && item.hiking) hasSelectedFacility = true;
        if (facilityFilters.birding && item.birding) hasSelectedFacility = true;
        if (facilityFilters.fishing && item.fishing) hasSelectedFacility = true;
        if (!hasSelectedFacility) return false;
      }
    }
    
    return true;
  });

  return (
    <div>
      {/* Enhanced Search Controls */}
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input 
            {...searchName} 
            placeholder="Search by name..." 
            className="h-11 rounded-lg bg-white/10 border border-white/20 px-4 text-white placeholder:text-white/60 focus:border-emerald-400 focus:outline-none transition-colors"
          />
          <input 
            {...zipCode} 
            placeholder="Zip Code (e.g., 22030)" 
            className="h-11 rounded-lg bg-white/10 border border-white/20 px-4 text-white placeholder:text-white/60 focus:border-emerald-400 focus:outline-none transition-colors"
          />
          <select 
            {...type} 
            className="h-11 rounded-lg bg-white/10 border border-white/20 px-4 text-white focus:border-emerald-400 focus:outline-none transition-colors"
          >
            {Object.entries(resourceTypes).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.label}</option>
            ))}
          </select>
          <Button 
            className="h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors" 
            onClick={fetchItems}
          >
            🔍 Search
          </Button>
        </div>
        
        {/* Facility Filters for Wildlife Areas */}
        {type.value === 'Wildlife Areas' && (
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="text-sm text-white/70 mb-2">Filter by Facilities:</div>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.camping}
                  onChange={(e) => setFacilityFilters({...facilityFilters, camping: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🏕️ Camping</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.boat}
                  onChange={(e) => setFacilityFilters({...facilityFilters, boat: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🚤 Boat Ramps</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.range}
                  onChange={(e) => setFacilityFilters({...facilityFilters, range: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🎯 Shooting Range</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.hiking}
                  onChange={(e) => setFacilityFilters({...facilityFilters, hiking: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🥾 Hiking Trails</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.birding}
                  onChange={(e) => setFacilityFilters({...facilityFilters, birding: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🦅 Birding</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="checkbox"
                  checked={facilityFilters.fishing}
                  onChange={(e) => setFacilityFilters({...facilityFilters, fishing: e.target.checked})}
                  className="rounded text-emerald-600"
                />
                <span>🎣 Fishing</span>
              </label>
            </div>
          </div>
        )}

        {/* View Mode Toggle & Results Count */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-white/70">
            {loading ? "Searching..." : `${filteredItems.length} resources found`}
            {favorites.size > 0 && (
              <span className="ml-2 text-yellow-400">• {favorites.size} favorites</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === "grid" ? "bg-emerald-600 text-white" : "bg-white/10 text-white/70 hover:text-white"
              }`}
            >
              ⊞ Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === "list" ? "bg-emerald-600 text-white" : "bg-white/10 text-white/70 hover:text-white"
              }`}
            >
              ☰ List
            </button>
          </div>
        </div>
      </div>

      {/* Results Display */}
      <div className="mt-6" data-testid="resources-list">
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="p-4 hh-card">
                <div className="h-4 w-32 bg-white/10 rounded mb-2" />
                <div className="h-3 w-24 bg-white/10 rounded mb-2" />
                <div className="h-3 w-full bg-white/10 rounded" />
              </Card>
            ))}
          </div>
        )}
        
        {!loading && (
          <>
            {viewMode === "grid" ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map((r, i) => (
                  <Card key={r.id || i} className="p-5 hh-card text-white hover:shadow-lg transition-all group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{resourceTypes[r.type]?.icon || '📍'}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${resourceTypes[r.type]?.color || 'bg-gray-600/30'}`}>
                          {r.type}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleFavorite(r.id)}
                        className="text-lg hover:scale-110 transition-transform"
                      >
                        {favorites.has(r.id) ? '⭐' : '☆'}
                      </button>
                    </div>
                    
                    <h3 className="font-semibold text-white mb-2 group-hover:text-emerald-300 transition-colors">
                      {r.name}
                    </h3>
                    
                    <div className="space-y-2 text-sm">
                      {/* Special display for Wildlife Areas */}
                      {r.type === 'Wildlife Areas' && (
                        <>
                          {r.county && (
                            <div className="text-white/70 text-xs">
                              <span>📍 {r.county}</span>
                              {r.acreage && <span> • {r.acreage.toLocaleString()} acres</span>}
                            </div>
                          )}
                          
                          {r.access_type && (
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                r.access_type === 'public' ? 'bg-green-600/30 text-green-300' :
                                r.access_type === 'private' ? 'bg-blue-600/30 text-blue-300' :
                                r.access_type === 'lease' ? 'bg-purple-600/30 text-purple-300' :
                                'bg-yellow-600/30 text-yellow-300'
                              }`}>
                                {r.access_type}
                              </span>
                            </div>
                          )}
                          
                          {/* Facilities & Activities */}
                          {(r.primitive_camping || r.boat_ramps || r.shooting_ranges || r.hiking || r.birding || r.fishing) && (
                            <div className="flex flex-wrap gap-1">
                              {r.primitive_camping && <span className="text-xs text-green-300">🏕️ Camping</span>}
                              {r.boat_ramps && <span className="text-xs text-blue-300">🚤 Boat</span>}
                              {r.shooting_ranges && <span className="text-xs text-orange-300">🎯 Range</span>}
                              {r.hiking && <span className="text-xs text-purple-300">🥾 Trails</span>}
                              {r.birding && <span className="text-xs text-yellow-300">🦅 Birding</span>}
                              {r.fishing && <span className="text-xs text-cyan-300">🎣 Fishing</span>}
                            </div>
                          )}
                          
                          {r.hunting_types && r.hunting_types.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {r.hunting_types.map((type, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-emerald-600/30 text-emerald-300 text-xs rounded">
                                  {type}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Regular display for non-Wildlife Areas */}
                      {r.type !== 'Wildlife Areas' && (
                        <>
                          {r.zip_code && (
                            <div className="flex items-center gap-2 text-white/70">
                              <span>📮</span>
                              <span className="font-mono text-emerald-400">Zip: {r.zip_code}</span>
                              {r.county && <span className="text-white/50">• {r.county}</span>}
                            </div>
                          )}
                          
                          {r.address && (
                            <div className="flex items-start gap-2 text-white/70">
                              <span>🗺️</span>
                              <div className="flex-1">
                                <p className="text-xs leading-relaxed">{r.address}</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {r.phone && (
                        <div className="flex items-center gap-2 text-white/70">
                          <span>📞</span>
                          <a href={`tel:${r.phone}`} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                            {formatPhone(r.phone)}
                          </a>
                        </div>
                      )}
                      
                      {r.url && (
                        <div className="flex items-center gap-2">
                          <span>🌐</span>
                          <a 
                            href={r.url} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-emerald-400 hover:text-emerald-300 text-xs underline transition-colors"
                          >
                            Official Website
                          </a>
                        </div>
                      )}
                      
                      {r.hours && (
                        <div className="flex items-center gap-2 text-white/70">
                          <span>🕒</span>
                          <span className="text-xs">{r.hours}</span>
                        </div>
                      )}
                      
                      {r.notes && (
                        <div className="flex items-start gap-2 text-white/60 mt-2 pt-2 border-t border-white/10">
                          <span>💡</span>
                          <span className="text-xs italic">{r.notes}</span>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredItems.map((r, i) => (
                  <Card key={r.id || i} className="p-4 hh-card text-white hover:shadow-lg transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{resourceTypes[r.type]?.icon || '📍'}</span>
                          <div>
                            <h3 className="font-semibold">{r.name}</h3>
                            <p className="text-sm text-white/70">{r.type} • {r.county || 'Statewide'}</p>
                          </div>
                        </div>
                        
                        <div className="hidden sm:flex items-center gap-4 text-sm text-white/70">
                          {r.phone && (
                            <a href={`tel:${r.phone}`} className="text-emerald-400 hover:text-emerald-300">
                              📞 {formatPhone(r.phone)}
                            </a>
                          )}
                          {/* Show facilities for Wildlife Areas instead of directions */}
                          {r.type === 'Wildlife Areas' && (
                            <div className="flex items-center gap-2">
                              {r.acreage && <span className="text-xs">🌲 {r.acreage.toLocaleString()} acres</span>}
                              {r.primitive_camping && <span className="text-xs">🏕️</span>}
                              {r.boat_ramps && <span className="text-xs">🚤</span>}
                              {r.shooting_ranges && <span className="text-xs">🎯</span>}
                              {r.fishing && <span className="text-xs">🎣</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {r.url && (
                          <a 
                            href={r.url} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-emerald-400 hover:text-emerald-300 text-sm underline"
                          >
                            Website
                          </a>
                        )}
                        <button
                          onClick={() => toggleFavorite(r.id)}
                          className="text-lg hover:scale-110 transition-transform"
                        >
                          {favorites.has(r.id) ? '⭐' : '☆'}
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            
            {filteredItems.length === 0 && !loading && (
              <Card className="p-8 hh-card text-center">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-lg font-semibold text-white mb-2">No Resources Found</h3>
                <p className="text-white/70 mb-4">
                  {searchName.value || zipCode.value || type.value !== 'All'
                    ? 'Try adjusting your search criteria or expanding your search area.'
                    : 'Local resource data is being populated for this state.'}
                </p>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    zipCode.set('');
                    type.set('All');
                    searchName.set('');
                    fetchItems();
                  }}
                >
                  Clear Filters
                </Button>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}


  const [seasonLinks, setSeasonLinks] = useState([]);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState("all");
  const [countySearch, setCountySearch] = useState('');
  const [seasonViewMode, setSeasonViewMode] = useState("calendar"); // "calendar" or "table"

  // Helper function to get unique species from season data
  const getUniqueSpecies = (seasons) => {
    const species = new Set();
    seasons.forEach(season => {
      if (season.species_group) {
        // Normalize casing - capitalize first letter
        const normalized = season.species_group.toLowerCase();
        const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
        species.add(capitalized);
      }
    });
    return Array.from(species).sort();
  };

  // Helper function to create unique key for deduplication
  const createSeasonKey = (season) => {
    const cleanTitle = cleanSeasonTitle(season.title);
    const extractedDates = extractDatesFromTitle(season.title);
    const dateKey = extractedDates.length > 0 ? extractedDates.join('|') : `${season.start_date}-${season.end_date}`;
    const areasKey = season.areas ? season.areas.sort().join('|') : 'statewide';
    
    return `${cleanTitle}|${season.method}|${dateKey}|${season.bag_limit || 'none'}|${season.weapon_restrictions || ''}|${season.special_requirements || ''}|${areasKey}`;
  };

  // Filter, deduplicate, and sort seasons
  const filteredAndDeduplicatedSeasons = (() => {
    // First apply filters
    const filtered = seasonLinks.filter(season => {
      // Species filter
      let speciesMatch = true;
      if (selectedSpecies !== "all") {
        if (!season.species_group) speciesMatch = false;
        else {
          const normalized = season.species_group.toLowerCase();
          const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
          speciesMatch = capitalized === selectedSpecies;
        }
      }
      
      // County search filter
      let countyMatch = true;
      if (countySearch.trim()) {
        const searchTerm = countySearch.toLowerCase().trim();
        if (season.areas && season.areas.length > 0) {
          countyMatch = season.areas.some(area => 
            area.toLowerCase().includes(searchTerm)
          );
        } else {
          // If no specific areas, consider it as "Statewide" and match if searching for statewide
          countyMatch = 'statewide'.includes(searchTerm);
        }
      }
      
      return speciesMatch && countyMatch;
    });

    // Then remove duplicates
    const seen = new Set();
    const deduplicated = filtered.filter(season => {
      const key = createSeasonKey(season);
      if (seen.has(key)) {
        return false; // Skip duplicate
      }
      seen.add(key);
      return true;
    });

    // Finally, sort by start date (earliest first), then by species name
    return deduplicated.sort((a, b) => {
      // Primary sort: by earliest start date
      const dateA = getEarliestSeasonDate(a);
      const dateB = getEarliestSeasonDate(b);
      const dateComparison = dateA.getTime() - dateB.getTime();
      
      if (dateComparison !== 0) {
        return dateComparison;
      }
      
      // Secondary sort: by species name (case-insensitive)
      const speciesA = (a.species_group || '').toLowerCase();
      const speciesB = (b.species_group || '').toLowerCase();
      return speciesA.localeCompare(speciesB);
    });
  })();

  // Use the deduplicated seasons
  const filteredSeasons = filteredAndDeduplicatedSeasons;
  
  // Calculate deduplicated total (all seasons without any species/county filters, but with deduplication)
  const deduplicatedTotal = (() => {
    const seen = new Set();
    return seasonLinks.filter(season => {
      const key = createSeasonKey(season);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).length;
  })();

  useEffect(() => {
    const requested = search.get('tab');
    if (requested && ['regulations','seasons','resources'].includes(requested)) {
      setTab(requested);
    }
  }, [search]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/state/${code}/hub`);
        if (!mounted) return;
        setHub(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load state hub");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [code]);

  useEffect(() => {
    let mounted = true;
    if (tab === 'seasons') {
      (async () => {
        try {
          setSeasonLoading(true);
          const res = await axios.get(`${API}/seasons/${code}`);
          if (!mounted) return;
          const items = (res.data?.items || []).sort((a,b)=>{
            const ta = a?.last_updated ? new Date(a.last_updated).getTime() : 0;
            const tb = b?.last_updated ? new Date(b.last_updated).getTime() : 0;
            return tb - ta;
          });
          console.log(`Loaded ${items.length} seasons for ${code}`);
          setSeasonLinks(items);
        } catch (e) {
          console.error('Failed to load seasons:', e);
          // non-fatal
        } finally {
          setSeasonLoading(false);
        }
      })();
    }
    return () => (mounted = false);
  }, [tab, code]);

  const refreshSeasons = async () => {
    try {
      setSeasonLoading(true);
      await axios.post(`${API}/seasons/refresh`, null, { params: { state_code: code } });
      const res = await axios.get(`${API}/seasons/${code}`);
      const items = (res.data?.items || []).sort((a,b)=>{
        const ta = a?.last_updated ? new Date(a.last_updated).getTime() : 0;
        const tb = b?.last_updated ? new Date(b.last_updated).getTime() : 0;
        return tb - ta;
      });
      setSeasonLinks(items);
      toast({ title: "Official links updated", description: `${items.length} links saved for ${code}` });
    } catch (e) {
      toast({ title: "Update failed", description: e?.message || "Try again later" });
    } finally {
      setSeasonLoading(false);
    }
  };

  const stateName = hub?.state?.name || code;
  const lastUpdated = seasonLinks.reduce((acc, it) => {
    if (!it?.last_updated) return acc;
    const t = new Date(it.last_updated).getTime();
    return Math.max(acc, t);
  }, 0);
  const lastUpdatedText = lastUpdated ? new Date(lastUpdated).toLocaleString() : '—';

  return (
    <div className="container mx-auto px-6 py-10" data-testid="state-hub-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-serif text-white" data-testid="state-hub-title">{stateName} Hunters' Hub</h1>
          <p className="text-white/70 text-sm mt-1" data-testid="state-hub-subtitle">Official links and guidance for {stateName}. Canada provinces coming soon.</p>
        </div>
        <Link to="/" data-testid="state-hub-back">
          <Button variant="outline" className="rounded-full bg-black/40 border-white/40 text-white hover:bg-black/60 hover:border-white/60 hover:text-white shadow-lg backdrop-blur-sm focus:ring-2 focus:ring-white/30">Back</Button>
        </Link>
      </div>

      {error && <p className="text-red-400 mt-6" data-testid="state-hub-error">{error}</p>}

      <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="state-hub-tabs">
        <TabsList className="grid grid-cols-3 gap-2 bg-white/10 border border-white/20" data-testid="state-hub-tabs-list">
          {['regulations','seasons','resources'].map(k => (
            <TabsTrigger key={k} value={k} className="data-[state=active]:bg-white/15 data-[state=active]:text-white text-white/80" data-testid={`tab-${k}`}>{titleMap[k]}</TabsTrigger>
          ))}
        </TabsList>

        {['regulations','seasons','resources'].map(k => (
          <TabsContent key={k} value={k} className="mt-6" data-testid={`tab-content-${k}`}>
            {loading && (
              <Card className="p-5 hh-card text-white" data-testid="state-hub-loading">
                <div className="h-5 w-48 bg-white/10 rounded" />
                <div className="h-3 w-80 bg-white/10 rounded mt-3" />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 bg-white/10 rounded" />
                  ))}
                </div>
              </Card>
            )}
            {!loading && hub && (
              <>
                <HubSection section={hub.sections?.[k]} />
                
                {/* Legal Hunting Hours Card - Display right after Seasons card */}
                {console.log('Tab:', k, 'Hunting Hours:', hub?.hunting_hours)}
                {k === 'seasons' && hub?.hunting_hours && (
                  <div className="mt-4">
                    <Card className="p-5 hh-card text-white" data-testid="hunting-hours-card">
                      <h2 className="text-xl font-semibold">🕐 Legal Hunting Hours</h2>
                      <p className="text-white/75 mt-2 text-sm whitespace-pre-line">
                        State-regulated shooting hours for all game species. Hunting outside these hours is a violation of state and federal law.
                      </p>
                      
                      {/* Warning Banner */}
                      <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-3 mt-3">
                        <p className="text-yellow-200 text-sm">
                          ⚠️ <strong>Important:</strong> Always verify current regulations and consult official sunrise/sunset tables for your specific hunting location.
                        </p>
                      </div>
                      
                      {/* Hours Grid */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {hub.hunting_hours.general && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🌅 General Hunting</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.general}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.big_game && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦌 Big Game</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.big_game}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.small_game && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🐰 Small Game</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.small_game}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.spring_turkey && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦃 Spring Turkey</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.spring_turkey}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.fall_turkey && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦃 Fall Turkey</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.fall_turkey}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.turkey && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦃 Turkey</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.turkey}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.youth_turkey && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦃 Youth Turkey</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.youth_turkey}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.migratory_birds && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦆 Migratory Birds</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.migratory_birds}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.waterfowl && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦆 Waterfowl</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.waterfowl}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.doves && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🕊️ Doves</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.doves}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.dove && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🕊️ Dove</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.dove}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.white_winged_dove && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🕊️ White-winged Dove</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.white_winged_dove}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.teal && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🦆 Teal</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.teal}</p>
                          </Card>
                        )}
                        
                        {hub.hunting_hours.night_hunting && (
                          <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                            <p className="font-medium">🌙 Night Hunting</p>
                            <p className="text-sm text-white/70 mt-1">{hub.hunting_hours.night_hunting}</p>
                          </Card>
                        )}
                      </div>
                      
                      {/* State-specific Notes */}
                      {hub.hunting_hours.notes && (
                        <div className="mt-4 p-3 bg-blue-400/10 rounded-lg border border-blue-400/30">
                          <p className="text-sm text-blue-200">
                            <strong>📋 State Notes:</strong> {hub.hunting_hours.notes}
                          </p>
                        </div>
                      )}
                    </Card>
                    <p className="text-xs text-white/60 mt-3">Hunting hours data current as of October 2025</p>
                  </div>
                )}
                
                {k === 'seasons' && (
                  <div className="mt-4" data-testid="season-links">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Hunting Seasons ({deduplicatedTotal} total)</h3>
                      <div className="text-xs text-white/60">Last updated: {lastUpdatedText}</div>
                      <Button data-testid="refresh-seasons" className="rounded-full" onClick={refreshSeasons}>Update official links</Button>
                    </div>
                    
                    {/* Filtering Controls */}
                    <div className="mb-4 flex flex-col lg:flex-row gap-4">
                      {/* Species Selector */}
                      <div className="flex-1">
                        <label className="text-sm text-white/80 mb-2 block">Select Species:</label>
                        <Select value={selectedSpecies} onValueChange={setSelectedSpecies}>
                          <SelectTrigger className="w-full lg:w-64 bg-black/40 border-white/40 text-white hover:bg-black/50 hover:border-white/50 shadow-lg backdrop-blur-sm focus:ring-2 focus:ring-white/30">
                            <SelectValue placeholder="All Species" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Species</SelectItem>
                            {getUniqueSpecies(seasonLinks).map(species => (
                              <SelectItem key={species} value={species}>{species}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* County Search */}
                      <div className="flex-1">
                        <label className="text-sm text-white/80 mb-2 block">Search by County:</label>
                        <Input
                          type="text"
                          placeholder="Enter county name..."
                          value={countySearch}
                          onChange={(e) => setCountySearch(e.target.value)}
                          className="w-full lg:w-64 bg-black/40 border-white/40 text-white placeholder:text-white/50 hover:bg-black/50 hover:border-white/50 shadow-lg backdrop-blur-sm focus:ring-2 focus:ring-white/30"
                        />
                      </div>
                      
                      {/* Clear Filters Button */}
                      <div className="flex-shrink-0 flex items-end">
                        <Button 
                          onClick={() => {
                            setSelectedSpecies('all');
                            setCountySearch('');
                          }}
                          variant="outline"
                          className="mb-0 bg-black/40 border-white/40 text-white hover:bg-black/60 hover:border-white/60 hover:text-white shadow-lg backdrop-blur-sm"
                          disabled={selectedSpecies === 'all' && !countySearch.trim()}
                        >
                          Clear Filters
                        </Button>
                      </div>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="mb-4 flex items-center gap-2 bg-black/40 rounded-lg p-2 w-fit">
                      <span className="text-sm text-white/80 ml-2">View:</span>
                      <button
                        onClick={() => setSeasonViewMode("calendar")}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                          seasonViewMode === "calendar" 
                            ? "bg-emerald-600 text-white" 
                            : "bg-white/10 text-white/70 hover:text-white hover:bg-white/20"
                        }`}
                      >
                        📅 Calendar
                      </button>
                      <button
                        onClick={() => setSeasonViewMode("table")}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                          seasonViewMode === "table" 
                            ? "bg-emerald-600 text-white" 
                            : "bg-white/10 text-white/70 hover:text-white hover:bg-white/20"
                        }`}
                      >
                        📋 Table
                      </button>
                    </div>

                    {seasonLoading && (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="season-skeleton">
                        {[...Array(6)].map((_,i)=>(<div key={i} className="h-20 bg-white/10 rounded"/>))}
                      </div>
                    )}
                    
                    {/* Calendar View */}
                    {!seasonLoading && seasonViewMode === "calendar" && (
                      <SeasonCalendar 
                        seasons={filteredSeasons} 
                        stateCode={code.toUpperCase()}
                      />
                    )}
                    
                    {/* Table View */}
                    {!seasonLoading && seasonViewMode === "table" && (
                      <div className="overflow-x-auto">
                        <table className="w-full hh-card rounded-lg overflow-hidden text-sm">
                          <thead>
                            <tr className="bg-black/40 border-b border-white/20">
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Season</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Method</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">License Requirements</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Dates</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Bag Limit</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Restrictions</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Counties</th>
                              <th className="text-left px-2 py-2 text-white font-semibold text-xs">Source</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredSeasons.map((it, idx) => {
                              const extractedDates = extractDatesFromTitle(it.title);
                              const cleanTitle = cleanSeasonTitle(it.title);
                              const dates = extractedDates.length > 0 ? extractedDates : [formatSeasonRange(it.start_date, it.end_date)];
                              
                              return (
                                <tr key={`${it.id}-${idx}`} className={`border-b border-white/10 ${idx % 2 === 0 ? 'bg-black/20' : 'bg-black/10'} hover:bg-black/30 transition-colors`}>
                                  <td className="px-2 py-2 text-white text-xs">
                                    <div className="min-w-[140px] font-medium" title={cleanTitle || 'Season'}>
                                      {cleanTitle || 'Season'}
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 text-white/90 text-xs">
                                    <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                      it.method === 'Archery' ? 'bg-green-600/30 text-green-200 border border-green-500/50' :
                                      it.method === 'Muzzleloader' ? 'bg-orange-600/30 text-orange-200 border border-orange-500/50' :
                                      it.method === 'Firearms' ? 'bg-red-600/30 text-red-200 border border-red-500/50' :
                                      'bg-gray-600/30 text-gray-200 border border-gray-500/50'
                                    }`}>
                                      {it.method || 'General'}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-white/90 text-xs">
                                    <div className="min-w-[200px] max-w-[280px]">
                                      {it.license_requirements ? (
                                        <div className="bg-cyan-600/20 text-cyan-200 px-1.5 py-0.5 rounded text-xs border border-cyan-500/30" title={it.license_requirements}>
                                          <div className="break-words">{it.license_requirements}</div>
                                        </div>
                                      ) : (
                                        <span className="text-white/50">Check regulations</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 text-white/90 text-xs">
                                    <div className="text-xs">
                                      <div className="font-medium space-y-0.5">
                                        {dates.map((dateRange, dateIdx) => (
                                          <div key={dateIdx} className="leading-snug whitespace-nowrap">
                                            {dateRange}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                <td className="px-2 py-2 text-white/90 text-xs">
                                  <div className="text-xs min-w-[80px]" title={it.bag_limit || ''}>
                                    {it.bag_limit ? (
                                      <span className="bg-blue-600/25 text-blue-200 px-1.5 py-0.5 rounded text-xs inline-block border border-blue-500/30">
                                        {it.bag_limit}
                                      </span>
                                    ) : (
                                      <span className="text-white/50">—</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-white/90 text-xs">
                                  <div className="min-w-[120px] max-w-[180px]">
                                    {it.weapon_restrictions || it.legal_methods || it.special_requirements ? (
                                      <div className="text-xs space-y-0.5">
                                        {it.weapon_restrictions && (
                                          <div className="bg-red-600/20 text-red-200 px-1.5 py-0.5 rounded text-xs border border-red-500/30" title={it.weapon_restrictions}>
                                            <div className="line-clamp-2">{it.weapon_restrictions}</div>
                                          </div>
                                        )}
                                        {it.special_requirements && (
                                          <div className="bg-yellow-600/20 text-yellow-200 px-1.5 py-0.5 rounded text-xs border border-yellow-500/30" title={it.special_requirements}>
                                            <div className="line-clamp-2">{it.special_requirements}</div>
                                          </div>
                                        )}
                                        {it.legal_methods && (
                                          <div className="bg-green-600/20 text-green-200 px-1.5 py-0.5 rounded text-xs border border-green-500/30" title={it.legal_methods}>
                                            <div className="line-clamp-2">{it.legal_methods}</div>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-white/50">—</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-white/90 text-xs">
                                  <div className="min-w-[100px] max-w-[140px]">
                                    {it.areas && it.areas.length > 0 ? (
                                      <div className="text-xs space-y-0.5" title={it.areas.join(', ')}>
                                        {it.areas.map((area, areaIdx) => (
                                          <div key={areaIdx}>
                                            <span className="bg-purple-600/20 text-purple-200 px-1.5 py-0.5 rounded text-xs border border-purple-500/30 whitespace-nowrap">
                                              {area}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-white/70 bg-gray-600/20 px-1.5 py-0.5 rounded text-xs border border-gray-500/30">Statewide</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-white/90 text-xs">
                                  <div className="min-w-[50px]">
                                    {it.source_url ? (
                                      <a href={it.source_url} target="_blank" rel="noreferrer" className="bg-blue-600/25 text-blue-200 hover:bg-blue-600/40 hover:text-blue-100 px-2 py-1 rounded text-xs border border-blue-500/30 transition-colors inline-block">
                                        View
                                      </a>
                                    ) : (
                                      <span className="text-white/50">—</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                            {filteredSeasons.length === 0 && (
                              <tr>
                                <td colSpan="8" className="p-8 text-center text-white/70">
                                  <div className="text-lg font-medium">
                                    {selectedSpecies === 'all' ? 'No seasons available.' : `No seasons found for ${selectedSpecies}.`}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        
                        {/* Enhanced summary info */}
                        <div className="mt-4 p-4 hh-card rounded-lg">
                          <div className="text-sm text-white/80 flex justify-between items-center">
                            <div>
                              <span className="font-medium">
                                {(selectedSpecies === 'all' && !countySearch.trim()) ? (
                                  // No filters active - show "X of X seasons"
                                  <>
                                    Showing {filteredSeasons.length} of {deduplicatedTotal} seasons
                                  </>
                                ) : (
                                  // Filters active - show filtered count of deduplicated total
                                  <>
                                    Showing {filteredSeasons.length} of {deduplicatedTotal} seasons
                                    <span className="text-yellow-300 ml-2">
                                      (filtered
                                      {selectedSpecies !== 'all' && ` by ${selectedSpecies}`}
                                      {countySearch.trim() && ` by "${countySearch.trim()}"`}
                                      )
                                    </span>
                                  </>
                                )}
                              </span>
                            </div>
                            {filteredSeasons.length > 0 && (
                              <div className="text-xs text-white/60" title="Data quality: Comprehensive = detailed official data with dates, bag limits, and restrictions; Detailed = parsed from regulations; Link-only = reference link">
                                <span className="text-green-300">{filteredSeasons.filter(s => s.extraction_quality === 'comprehensive').length} complete</span>
                                {filteredSeasons.filter(s => s.extraction_quality === 'parsed-exact').length > 0 && (
                                  <span>, <span className="text-blue-300">{filteredSeasons.filter(s => s.extraction_quality === 'parsed-exact').length} detailed</span></span>
                                )}
                                {filteredSeasons.filter(s => s.extraction_quality === 'link-only').length > 0 && (
                                  <span>, <span className="text-gray-300">{filteredSeasons.filter(s => s.extraction_quality === 'link-only').length} links</span></span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {k === 'resources' && (
                  <div className="mt-6" data-testid="resources-county-scaffold">
                    <Card className="p-6 hh-card text-white">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">🗺️</span>
                        <div>
                          <h3 className="text-xl font-semibold">Local Hunting Resources Directory</h3>
                          <p className="text-sm text-white/75">Find wildlife areas, license agents, shooting ranges, and hunter education classes near you</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-6">
                        <button 
                          onClick={() => setResourceFilter("Wildlife Areas")}
                          className={`text-center p-3 rounded-lg border transition-all cursor-pointer group ${
                            resourceFilter === "Wildlife Areas" 
                              ? "bg-green-600/40 border-green-400/70 ring-2 ring-green-400/50" 
                              : "bg-green-600/20 border-green-500/30 hover:bg-green-600/30 hover:border-green-400/50"
                          }`}
                        >
                          <div className="text-xl mb-1 group-hover:scale-110 transition-transform">🌲</div>
                          <div className="text-xs text-green-200 font-medium group-hover:text-green-100">Wildlife Areas</div>
                        </button>
                        <button 
                          onClick={() => setResourceFilter("License Agents")}
                          className={`text-center p-3 rounded-lg border transition-all cursor-pointer group ${
                            resourceFilter === "License Agents"
                              ? "bg-blue-600/40 border-blue-400/70 ring-2 ring-blue-400/50"
                              : "bg-blue-600/20 border-blue-500/30 hover:bg-blue-600/30 hover:border-blue-400/50"
                          }`}
                        >
                          <div className="text-xl mb-1 group-hover:scale-110 transition-transform">📋</div>
                          <div className="text-xs text-blue-200 font-medium group-hover:text-blue-100">License Agents</div>
                        </button>
                        <button 
                          onClick={() => setResourceFilter("Shooting Ranges")}
                          className={`text-center p-3 rounded-lg border transition-all cursor-pointer group ${
                            resourceFilter === "Shooting Ranges"
                              ? "bg-red-600/40 border-red-400/70 ring-2 ring-red-400/50"
                              : "bg-red-600/20 border-red-500/30 hover:bg-red-600/30 hover:border-red-400/50"
                          }`}
                        >
                          <div className="text-xl mb-1 group-hover:scale-110 transition-transform">🎯</div>
                          <div className="text-xs text-red-200 font-medium group-hover:text-red-100">Shooting Ranges</div>
                        </button>
                        <button 
                          onClick={() => setResourceFilter("Hunter Ed / Classes")}
                          className={`text-center p-3 rounded-lg border transition-all cursor-pointer group ${
                            resourceFilter === "Hunter Ed / Classes"
                              ? "bg-purple-600/40 border-purple-400/70 ring-2 ring-purple-400/50"
                              : "bg-purple-600/20 border-purple-500/30 hover:bg-purple-600/30 hover:border-purple-400/50"
                          }`}
                        >
                          <div className="text-xl mb-1 group-hover:scale-110 transition-transform">🎓</div>
                          <div className="text-xs text-purple-200 font-medium group-hover:text-purple-100">Hunter Education</div>
                        </button>
                      </div>
                      
                      {resourceFilter !== "All" && (
                        <div className="mb-4 text-center">
                          <button
                            onClick={() => setResourceFilter("All")}
                            className="text-sm text-emerald-400 hover:text-emerald-300 underline"
                          >
                            Show All Resources
                          </button>
                        </div>
                      )}
                      
                      <ResourcesControls stateCode={code} initialFilter={resourceFilter} key={resourceFilter} />
                    </Card>
                  </div>
                )}
              </>
            )}
            {!loading && !hub && !error && (
              <Card className="p-5 hh-card text-white">
                <p>Data not available. Please try again.</p>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

const titleMap = {
  regulations: 'Regulations',
  seasons: 'Seasons',
  resources: 'Local Resources',
};

function HubSection({ section }) {
  if (!section) return null;
  return (
    <div>
      <Card className="p-5 hh-card text-white" data-testid="hub-section-card">
        <h2 className="text-xl font-semibold">{section.title}</h2>
        <p className="text-white/75 mt-2 text-sm whitespace-pre-line" data-testid="hub-section-summary">{section.summary}</p>
        {Array.isArray(section.bullets) && section.bullets.length > 0 && (
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm text-white/85" data-testid="hub-section-bullets">
            {section.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {section.links.map((l, idx) => (
            <a key={idx} href={l.url} target="_blank" rel="noreferrer" data-testid={`hub-link-${idx}`}>
              <Card className="p-4 hover:shadow-md transition-shadow hh-card text-white">
                <p className="font-medium">{l.title}</p>
                {l.description && <p className="text-sm text-white/70 mt-1">{l.description}</p>}
              </Card>
            </a>
          ))}
        </div>
      </Card>
      <p className="text-xs text-white/60 mt-3" data-testid="hub-last-refreshed">Last refreshed: {new Date().toLocaleString()}</p>
    </div>
  );
}
